import { useQuery } from '@tanstack/vue-query'
import type { ComputedRef, Ref } from 'vue'
import { computed } from 'vue'

import { getGroup, GROUP_QUERY_KEY } from '@/features/groups/api'
import type { V1GroupDataType, V2GroupType } from '@/features/groups/types'

function V1ToV2DataMiddleware({ group }: { group: V1GroupDataType | undefined }): V2GroupType | null {
  if (!group) return null
  return {
    id: group.id,
    idByMentor: group.idByMentor,
    conversationId: group.conversationId,
    meetingRoomId: group.meetingRoomId,
    programId: group.programId,
    name: group.customName ?? group.name,
    users: group.groupUsers.map((groupUser) => ({
      id: groupUser.user.id,
      firstName: groupUser.user.identity?.firstname,
      lastName: groupUser.user.identity?.lastname,
      picture: groupUser.user.personalInformation?.profilPicture,
      email: groupUser.user.email,
      roles: groupUser.roles,
    })),
    completedSessions: group.sessions.length,
  } satisfies V2GroupType
}

const useGroup = (groupIdRef: ComputedRef<string | null> | Ref<string | null>) => {
  const isGroupQueryEnabledRef = computed(() => !!groupIdRef.value)

  const { data, isPending } = useQuery({
    queryKey: [GROUP_QUERY_KEY, { groupId: groupIdRef }],
    queryFn: () => getGroup({ groupId: groupIdRef.value! }),
    enabled: isGroupQueryEnabledRef,
  })

  const groupRef = computed(() => V1ToV2DataMiddleware({ group: data.value }))
  return {
    groupRef,
    isPendingRef: isPending,
  }
}

export default useGroup
